import client, { uploadVideo, uploadFile, PaginatedResponse } from '@/utils/client'
import urls from '@/utils/urls'

export const LIKE = 1
export const DISLIKE = -1

export const videoVisibilities = {
  draft: 'Draft (visible only to you)',
  private: 'Private (visible only to your fans)',
  public: 'Public (visible to everyone)'
}

export const albumVisibilities = {
  draft: 'Draft (visible only to you)',
  public: 'Published (visible to everyone)'
}

export const videoTypes = {
  'music-video': 'Music Video',
  live: 'Live Performance',
  image: 'Audio + Image',
  lyrics: 'Lyrics'
}

var defaultState = {
  tags: null,
  accessible: null
}

export const mutations = {
  setTags (state, tags) {
    state.tags = tags
  },
  setSongsWithAccess (state, songs) {
    state.accessible = songs
  }
}

export const getters = {
  tags: state => state.tags || []
}

export const actions = {
  listTags ({state, commit}) {
    if (state.tags) return Promise.resolve(state.tags)
    return client
      .get(urls.tag.list())
      .then(response => {
        commit('setTags', response.data)
        return response.data
      })
  },
  listOwnSongs (ctx, filter) {
    return client.get(urls.song.publish.list(), {
      params: filter
    }).then(response => new PaginatedResponse(response.data))
  },
  getOwnSong (ctx, {id}) {
    return client.get(urls.song.publish.detail(id)).then(response => response.data)
  },
  createSong (ctx, data) {
    if (data.id) throw new Error('song id is defined, can\'t create anew')
    return client.post(urls.song.publish.list(), data).then(response => response.data)
  },
  updateSong (ctx, data) {
    if (!data.id) throw new Error('song id undefined, can\'t update song')
    return client.patch(urls.song.publish.detail(data.id), data).then(response => response.data)
  },
  deleteSong (ctx, {id}) {
    return client.delete(urls.song.publish.detail(id)).then(response => response.data)
  },
  setVideoThumbnail ({dispatch}, {id, file}) {
    return uploadFile(urls.song.publish.thumbnail(id), file)
      .then(() => {
        return dispatch('getOwnSong', {id})
      })
  },
  deleteVideoThumbnail ({dispatch}, {id}) {
    return client
      .delete(urls.song.publish.thumbnail(id))
      .then(() => {
        return dispatch('getOwnSong', {id})
      })
  },
  uploadVideo (ctx, {id, file}) {
    return client
      .post(urls.song.publish.video(id))
      .then(response => {
        return uploadVideo(response.data, file)
      })
      .then(response => {
        return response.data
      })
  },
  deleteVideo (ctx, {id}) {
    return client
      .delete(urls.song.publish.detail(id))
      .then(response => {
        return response.data
      })
  },
  setAudioTrack ({dispatch}, {id, file}) {
    var fileType = file.type
    // Browsers don't know that we're dealing with audio-only mp4 files so they
    // may incorrectly set the content type to video for a file that's in fact audio
    if (fileType === 'video/mp4') fileType = 'audio/mp4'

    return uploadFile(urls.song.publish.audio(id), file, fileType)
      .then(() => {
        return dispatch('getOwnSong', {id})
      })
  },
  deleteAudioTrack ({dispatch}, {id}) {
    return client
      .delete(urls.song.publish.audio(id))
      .then(() => {
        return dispatch('getOwnSong', {id})
      })
  },
  listSongs (ctx, filter) {
    return client
      .get(urls.song.consume.list(), {
        params: filter
      })
      .then(response => new PaginatedResponse(response.data))
  },
  getSong (ctx, {id}) {
    return client.get(urls.song.consume.detail(id)).then(response => response.data)
  },
  async purchaseSong ({ dispatch }, {id, amount}) {
    let response = await client.post(urls.song.consume.purchase(id), {amount})
    await dispatch('refreshSongsWithAccess')
    return response.data
  },
  async refreshSongsWithAccess ({ commit }) {
    let tracks = []
    let response
    try {
      response = new PaginatedResponse((await client.get(urls.song.consume.listAccess())).data)
    } catch (e) {
      commit('setSongsWithAccess', [])
      return []
    }
    while (response.count) {
      for (let track of response.results) tracks.push(track)
      response = response.next()
    }
    commit('setSongsWithAccess', tracks)
    return tracks
  },
  async getSongsWithAccess ({state, dispatch}) {
    if (state.accessible !== null) {
      return state.accessible
    } else {
      return dispatch('refreshSongsWithAccess')
    }
  },
  likeSong (ctx, {id}) {
    return client.post(urls.song.consume.rate(id), {rating: LIKE}).then(response => response.data)
  },
  dislikeSong (ctx, {id}) {
    return client.post(urls.song.consume.rate(id), {rating: DISLIKE}).then(response => response.data)
  },
  unlikeSong (ctx, {id}) {
    return client.delete(urls.song.consume.rate(id)).then(response => response.data)
  },
  async reportSong ({ dispatch }, { id, reason, url }) {
    return client.post(urls.song.consume.report(id), { reason, url })
  },
  async reportSongComment ({ dispatch }, { subjectId, commentId, reason, url }) {
    return client.post(urls.song.consume.reportComment(subjectId, commentId), { reason, url })
  },
  recordView (ctx, {id}) {
    return client.post(urls.song.consume.touch(id))
  },
  getSongComments (ctx, {id}) {
    return client.get(urls.song.consume.commentList(id)).then(response => new PaginatedResponse(response.data))
  },
  postSongComment (ctx, {id, text, asArtist = false}) {
    return client
      .post(urls.song.consume.commentList(id), {text, as_artist: asArtist})
      .then(response => response.data)
  },
  deleteSongComment (ctx, {songId, commentId}) {
    return client
      .delete(urls.song.consume.commentDetail(songId, commentId))
      .then(response => response.data)
  },
  createAlbum (ctx, data) {
    if (data.id) throw new Error('Album already exists')
    return client
      .post(urls.album.list(), data)
      .then(response => response.data)
  },
  updateAlbum (ctx, data) {
    if (!data.id) throw new Error('Can\'t update nonexistent album')
    return client
      .patch(urls.album.detail(data.id), data)
      .then(response => response.data)
  },
  deleteAlbum (ctx, {id}) {
    return client.delete(urls.album.detail(id)).then(response => response.data)
  },
  setAlbumCover ({dispatch}, {id, file}) {
    return uploadFile(urls.album.cover(id), file)
      .then(() => {
        return dispatch('getAlbum', {id})
      })
  },
  deleteAlbumCover ({dispatch}, {id}) {
    return client.delete(urls.album.cover(id)).then(response => response.data)
  },
  listAlbums (ctx, filter) {
    return client
      .get(urls.album.list(), {
        params: filter
      })
      .then(response => new PaginatedResponse(response.data))
  },
  getAlbum (ctx, {id}) {
    return client.get(urls.album.detail(id)).then(response => response.data)
  },
  async purchaseAlbum ({dispatch}, {id, amount}) {
    let response = await client.post(urls.album.purchase(id), {amount})
    await dispatch('refreshSongsWithAccess')
    return response.data
  }
}

export default {
  namespaced: true,
  state: defaultState,
  mutations: mutations,
  getters: getters,
  actions: actions
}



// WEBPACK FOOTER //
// ./src/store/modules/song.js